// Thin fetch wrapper for the Xin API. Enforces:
//   - REACT_APP_BACKEND_URL only, no hardcoded hosts
//   - credentials: 'include' so admin cookies flow
//   - JSON in/out; multipart for /submissions
//   - Uniform error envelope {code, message, field?} → thrown as ApiError

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

export class ApiError extends Error {
  constructor(status, body) {
    // FastAPI HTTPException wraps our envelope in `detail`.
    // Support both shapes: {code,message,field} or {detail:{code,message,field}}.
    const env = (body && body.detail && typeof body.detail === 'object' && !Array.isArray(body.detail))
      ? body.detail
      : (body || {});
    // Pydantic validation errors come back as {detail:[{loc,msg,type},...]}
    let msg = env && env.message;
    if (!msg && body && Array.isArray(body.detail) && body.detail[0]) {
      const first = body.detail[0];
      const loc = Array.isArray(first.loc) ? first.loc.filter((x) => x !== 'body').join('.') : '';
      msg = loc ? `${loc}: ${first.msg}` : first.msg;
    }
    super(msg || `HTTP ${status}`);
    this.status = status;
    this.code = (env && env.code) || 'error';
    this.field = env && env.field;
    this.body = body;
  }
}

async function req(path, opts = {}) {
  // Retry policy: 5xx / 429 / fetch failures get one automatic retry with a
  // short backoff. This absorbs the transient Cloudflare 520s that happen
  // during backend cold-starts or short ingress blips — a diner tapping
  // "Vote" shouldn't have to see an error and re-tap in a 300ms window.
  // Every server write we retry is idempotent by design (IP-hash dedupe
  // on votes, email dedupe on subscribes, etc.), so double-fires are safe.
  //
  // AI endpoints pass { noRetry: true } because retrying a 20-60s LLM call
  // doubles the wait and often produces duplicate work.
  //
  // Admin 401 policy: if a request comes back 401 with code "unauthenticated"
  // AND this is not itself the /admin/login or /admin/refresh endpoint,
  // silently try POST /admin/refresh once — if the refresh cookie is still
  // valid the server mints a fresh access cookie and we retry the original
  // request transparently. Otherwise we broadcast a "xin-admin-signed-out"
  // event so the shell can bounce to the login card.
  const { noRetry, _isRefreshRetry, ...fetchOpts } = opts;
  const doFetch = () => fetch(BASE + path, {
    credentials: 'include',
    ...fetchOpts,
    headers: {
      Accept: 'application/json',
      ...(fetchOpts.body && !(fetchOpts.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(fetchOpts.headers || {}),
    },
  });

  let res;
  let networkErr = null;
  try {
    res = await doFetch();
  } catch (e) {
    networkErr = e;
  }
  const shouldRetry = !noRetry && (networkErr || (res && (res.status >= 500 || res.status === 429)));
  if (shouldRetry) {
    await new Promise((r) => setTimeout(r, 700));
    try {
      res = await doFetch();
      networkErr = null;
    } catch (e) {
      networkErr = e;
    }
  }

  // Convert a hard network failure into a friendly ApiError (still throws).
  if (networkErr || !res) {
    throw new ApiError(0, { code: 'network', message: 'Connection hiccup. Please check your network and try again.' });
  }

  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json().catch(() => null) : null;

  // Silent access-cookie refresh on 401 for admin endpoints.
  const isAdminReq = path.startsWith('/admin/');
  const isAuthEndpoint = path === '/admin/login' || path === '/admin/refresh' || path === '/admin/logout';
  const detail = (body && body.detail && typeof body.detail === 'object') ? body.detail : (body || {});
  const isUnauth = res.status === 401 && detail && detail.code === 'unauthenticated';
  if (isUnauth && isAdminReq && !isAuthEndpoint && !_isRefreshRetry) {
    // Try to refresh silently.
    let refreshOk = false;
    try {
      const rr = await fetch(BASE + '/admin/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      refreshOk = rr.ok;
    } catch (_e) {
      refreshOk = false;
    }
    if (refreshOk) {
      // Retry the original request with the flag so we don't loop.
      return req(path, { ...opts, _isRefreshRetry: true });
    }
    // Refresh failed — broadcast so the shell can bounce to login.
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('xin-admin-signed-out', {
        detail: { reason: 'session_expired' },
      }));
    }
  }

  if (!res.ok) {
    // Cloudflare 5xx pages come back as HTML with no JSON envelope. Give
    // the diner human-readable copy so they don't see raw "HTTP 520".
    if (!body) {
      const friendly =
        res.status === 520 || res.status === 502 || res.status === 503 || res.status === 504
          ? 'Server was briefly unreachable. Please try that again in a moment.'
          : res.status === 429
            ? 'You’re moving faster than the kitchen — pause a moment and try again.'
            : res.status === 413
              ? 'That upload is too large for our current limit.'
              : `Something went wrong (${res.status}). Please try again.`;
      throw new ApiError(res.status, { code: `http_${res.status}`, message: friendly });
    }
    throw new ApiError(res.status, body);
  }
  return body;
}

export const api = {
  // ---- Public reads ----
  outlets:   () => req('/outlets'),
  card:      (code) => req(`/cards/${encodeURIComponent(code)}`),
  trendingCard: (days = 30) => req('/trending-card' + qs({ days })),
  canvas:    (params = {}) => req('/canvas' + qs(params)),
  canvasIdea: (id) => req(`/canvas/idea/${encodeURIComponent(id)}`),
  canvasIdeaThread: (id) => req(`/canvas/idea/${encodeURIComponent(id)}/thread`),
  canvasIdeaReply: (id, form) => req(`/canvas/idea/${encodeURIComponent(id)}/reply`, { method: 'POST', body: form }),
  showcase:  (params = {}) => req('/showcase' + qs(params)),
  challenges: (city) => req('/challenges' + qs(city ? { city } : {})),
  madeReal:  (params = {}) => req('/made-real' + qs(params)),
  madeRealDetail: (code) => req(`/made-real/${encodeURIComponent(code)}`),

  // ---- Capture ----
  tsToken:   (card_code) => req(`/submissions/ts_token?card_code=${encodeURIComponent(card_code)}`),
  submit:    (formData) => req('/submissions', { method: 'POST', body: formData }),
  vote:      (id) => req(`/submissions/${encodeURIComponent(id)}/vote`, { method: 'POST', body: JSON.stringify({}) }),

  // ---- Admin auth ----
  adminLogin:   (email, password) => req('/admin/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  adminTotp:    (code) => req('/admin/totp', { method: 'POST', body: JSON.stringify({ code }) }),
  adminRefresh: () => req('/admin/refresh', { method: 'POST' }),
  adminLogout:  () => req('/admin/logout', { method: 'POST' }),
  adminMe:      () => req('/admin/me'),

  // ---- Admin inbox / curation ----
  adminInbox:      (params = {}) => req('/admin/inbox' + qs(params)),
  adminInboxCounts:() => req('/admin/inbox/counts'),
  adminLocate:     (id) => req(`/admin/submissions/${encodeURIComponent(id)}/locate`),
  adminTurnstileHealth: () => req('/admin/health/turnstile'),
  // ---- Diner Reels ----
  dinerReelSubmit: (payload) => req('/diner-reels', { method: 'POST', body: JSON.stringify(payload) }),
  dinerReelsApproved: (limit = 12) => req(`/diner-reels/approved?limit=${limit}`),
  dinerReelView: (id) => req(`/diner-reels/${id}/view`, { method: 'POST' }),
  dinerReelOfWeek: () => req('/diner-reels/reel-of-week'),
  adminDinerReels: (status) => req('/admin/diner-reels' + (status ? `?status=${status}` : '')),
  adminDinerReelPatch: (id, body) => req(`/admin/diner-reels/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDinerReelCrown: (id) => req(`/admin/diner-reels/${id}/crown`, { method: 'POST' }),
  adminDinerReelAutoCrown: () => req('/admin/diner-reels/auto-crown', { method: 'POST' }),
  adminDinerReelDelete: (id) => req(`/admin/diner-reels/${id}`, { method: 'DELETE' }),

  // --- Weekly Crown Email ---
  adminWeeklyCrownPreview: () => req('/admin/weekly-crown/preview'),
  adminWeeklyCrownRun: (force = false) => req('/admin/weekly-crown/run', { method: 'POST', body: JSON.stringify({ force }) }),
  adminWeeklyCrownClaims: (limit = 50) => req(`/admin/weekly-crown/claims?limit=${limit}`),
  adminWeeklyCrownRedeem: (cid, note) => req(`/admin/weekly-crown/claims/${cid}/redeem`, { method: 'POST', body: JSON.stringify({ note: note || null }) }),
  adminWeeklyCrownSetEmail: (rid, email) => req(`/admin/weekly-crown/reels/${rid}/email`, { method: 'POST', body: JSON.stringify({ email }) }),

  // --- Storyteller Calendar ---
  storytellerQueueList: (status = '') => req(`/admin/storyteller/queue${status ? `?status=${status}` : ''}`),
  storytellerQueuePatch: (qid, patch) => req(`/admin/storyteller/queue/${qid}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  storytellerQueueDelete: (qid) => req(`/admin/storyteller/queue/${qid}`, { method: 'DELETE' }),
  storytellerCalendarImport: async (file) => {
    const fd = new FormData(); fd.append('file', file);
    return req('/admin/storyteller/calendar/import', { method: 'POST', body: fd, headers: {} });
  },
  storytellerCalendarSeed: (name = 'aug_oct_2026') => req(`/admin/storyteller/calendar/seed-bundled?name=${encodeURIComponent(name)}`, { method: 'POST' }),
  storytellerCalendarSeedDownloadUrl: (name = 'feb_may_2026') => `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/,'')}/api/admin/storyteller/calendar/download-seed?name=${encodeURIComponent(name)}`,
  storytellerUploadMedia: async (qid, file) => {
    const fd = new FormData(); fd.append('file', file);
    return req(`/admin/storyteller/calendar/queue/${qid}/upload-media`, { method: 'POST', body: fd, headers: {} });
  },
  storytellerGenerateImage: (qid, opts) => req(`/admin/storyteller/calendar/queue/${qid}/generate-image`, { method: 'POST', body: JSON.stringify(opts || {}) }),
  storytellerGenerateVideo: (qid, opts) => req(`/admin/storyteller/calendar/queue/${qid}/generate-video`, { method: 'POST', body: JSON.stringify(opts || {}) }),

  // --- Brand Settings (owner-editable AI prompt anchor) ---
  brandSettingsGet: () => req('/admin/brand-settings'),
  brandSettingsPatch: (patch) => req('/admin/brand-settings', { method: 'PATCH', body: JSON.stringify(patch) }),
  brandSettingsReset: (which) => req('/admin/brand-settings/reset', { method: 'POST', body: JSON.stringify({ which }) }),
  storytellerSendDigestNow: (opts) => req('/admin/storyteller/calendar/digest/send-now', { method: 'POST', body: JSON.stringify(opts || {}) }),

  // --- Frame Studio ---
  frameStudioListFrames: () => req('/admin/frame-studio/frames'),
  frameStudioRender: async (spec, { signal } = {}) => {
    // Returns an object URL (blob) for the rendered PNG so <img src={url}/> works.
    // Accepts an AbortSignal so callers can cancel a previous debounced
    // render when a new keystroke queues the next one — keeps the server
    // from processing 10 stale composites (which used to pile up under
    // Cloudflare's 100 s edge and trigger a 520 popup).
    const url = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/,'')}/api/admin/frame-studio/render`;
    const r = await fetch(url, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(spec),
      signal,
    });
    if (!r.ok) throw new Error(`Render failed: HTTP ${r.status}`);
    const blob = await r.blob();
    return URL.createObjectURL(blob);
  },
  frameStudioPublish: (spec) => req('/admin/frame-studio/publish', { method: 'POST', body: JSON.stringify(spec) }),
  frameStudioUploadPhoto: async (file) => {
    const fd = new FormData(); fd.append('file', file);
    // Explicit fetch (not `req`) so we can turn the Cloudflare 520 (which
    // has no JSON body) into a friendly message instead of the raw HTML
    // parse error that used to bubble up as an alert(). The backend now
    // uses asyncio.to_thread so 520s should be rare, but if the upstream
    // ever times out again the operator gets a hint they can act on.
    const url = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/,'')}/api/admin/frame-studio/upload-photo`;
    const r = await fetch(url, { method: 'POST', credentials: 'include', body: fd });
    if (!r.ok) {
      if (r.status === 520 || r.status === 502 || r.status === 504) {
        throw new Error('The upload timed out at the network edge. Check the internet connection and try again — the file size is fine, the server auto-shrinks big photos.');
      }
      let msg = `Upload failed: HTTP ${r.status}`;
      try {
        const j = await r.json();
        msg = j?.detail?.message || j?.message || msg;
      } catch { /* non-JSON body — keep default */ }
      throw new Error(msg);
    }
    return await r.json();
  },
  frameStudioGeneratePhoto: (opts) => req('/admin/frame-studio/generate-photo', { method: 'POST', body: JSON.stringify(opts || {}) }),
  frameStudioLoadIdeaCard: (cardCode) => req(`/admin/frame-studio/idea-card/${encodeURIComponent(cardCode)}`),
  frameStudioPublishToMeta: (payload) => req('/admin/frame-studio/publish-to-meta', { method: 'POST', body: JSON.stringify(payload || {}) }),
  storytellerCalendarQueueGet: (qid) => req(`/admin/storyteller/calendar/queue/${qid}`),

  // --- Meta Token Health ---
  metaTokenHealth: () => req('/admin/meta/token-health'),
  metaTokenCheckNow: () => req('/admin/meta/token-health/check-now', { method: 'POST' }),
  storytellerSelectMedia: (qid, url) => req(`/admin/storyteller/calendar/queue/${qid}/select-media`, { method: 'POST', body: JSON.stringify({ url }) }),
  storytellerRemoveMedia: (qid, url) => req(`/admin/storyteller/calendar/queue/${qid}/remove-media`, { method: 'POST', body: JSON.stringify({ url }) }),
  storytellerApprove: (qid) => req(`/admin/storyteller/calendar/queue/${qid}/approve`, { method: 'POST' }),
  storytellerSkip: (qid) => req(`/admin/storyteller/calendar/queue/${qid}/skip`, { method: 'POST' }),
  storytellerCreateBlank: (opts) => req('/admin/storyteller/calendar/blank', { method: 'POST', body: JSON.stringify(opts || {}) }),
  storytellerIdeaCards: () => req('/admin/storyteller/calendar/idea-cards'),
  storytellerFillFromCard: (qid, card_code) => req(`/admin/storyteller/calendar/queue/${qid}/fill-from-card`, { method: 'POST', body: JSON.stringify({ card_code }) }),

  // --- Music Track Library ---
  musicTracksList: () => req('/admin/music-tracks'),
  musicTracksUpload: async (file, title, genre) => {
    const fd = new FormData(); fd.append('file', file); fd.append('title', title); if (genre) fd.append('genre', genre);
    return req('/admin/music-tracks', { method: 'POST', body: fd, headers: {} });
  },
  musicTracksDelete: (id) => req(`/admin/music-tracks/${id}`, { method: 'DELETE' }),
  musicTracksApply: (video_url, track_public_id) => req('/admin/music-tracks/apply', { method: 'POST', body: JSON.stringify({ video_url, track_public_id }) }),
  storytellerPublishNow: (qid) => req(`/admin/storyteller/queue/${qid}/publish`, { method: 'POST' }),

  adminPatch:      (id, body) => req(`/admin/submissions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDelete:     (id) => req(`/admin/submissions/${id}`, { method: 'DELETE' }),
  adminBulk:       (ids, action) => req('/admin/submissions/bulk', { method: 'POST', body: JSON.stringify({ ids, action }) }),
  adminGift:       (id, tier, note) => req(`/admin/submissions/${id}/reward`, { method: 'POST', body: JSON.stringify({ tier, note: note || null }) }),
  adminRedeemLookup: (code) => req(`/admin/redeem/${encodeURIComponent(code)}`),
  adminRedeem:     (claim_code, redeemed_outlet) => req('/admin/redeem', { method: 'POST', body: JSON.stringify({ claim_code, redeemed_outlet }) }),
  adminCards:      () => req('/admin/cards'),
  adminCardCreate: (formData) => req('/admin/cards', { method: 'POST', body: formData }),
  adminCardDraft:  (brief, deck) => req('/admin/cards/draft', { method: 'POST', body: JSON.stringify({ brief, deck }) }),
  adminCardPatch:  (code, formData) => req(`/admin/cards/${encodeURIComponent(code)}`, { method: 'PATCH', body: formData }),
  adminCardRegenImage: (code, tweak) => req(`/admin/cards/${encodeURIComponent(code)}/regen-image`, { method: 'POST', body: JSON.stringify({ tweak: tweak || '' }) }),
  adminCardGenImage: ({ headline, problem, deck, tweak }) => req('/admin/cards/gen-image', { method: 'POST', body: JSON.stringify({ headline, problem, deck, tweak: tweak || '' }) }),
  adminCardDelete: (code) => req(`/admin/cards/${encodeURIComponent(code)}`, { method: 'DELETE' }),
  adminCardExportURL: () => `${BASE}/admin/cards/export`,
  adminCardImport: (formData) => req('/admin/cards/import', { method: 'POST', body: formData }),
  adminMetrics:    (month) => req('/admin/metrics' + qs(month ? { month } : {})),
  adminMetricsLive: (days = 14) => req('/admin/metrics/live' + qs({ days })),
  adminShowcase:   (body) => req('/admin/showcase', { method: 'POST', body: JSON.stringify(body) }),
  adminShowcasePatch: (id, body) => req(`/admin/showcase/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminShowcaseDelete: (id) => req(`/admin/showcase/${id}`, { method: 'DELETE' }),
  adminCloseCycle: (month, cycle_note) => req('/admin/cycles/close', { method: 'POST', body: JSON.stringify({ month, cycle_note }) }),

  // ---- Admin Made Real Register (Addendum 9) ----
  adminMadeRealList: () => req('/admin/made-real'),
  adminMadeRealCreate: (body) => req('/admin/made-real', { method: 'POST', body: JSON.stringify(body) }),
  adminMadeRealPatch: (id, body) => req(`/admin/made-real/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminMadeRealDelete: (id) => req(`/admin/made-real/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminMadeRealPhotoUpload: (id, formData) => req(`/admin/made-real/${encodeURIComponent(id)}/photos`, { method: 'POST', body: formData }),
  adminMadeRealPhotoDelete: (id, url) => req(`/admin/made-real/${encodeURIComponent(id)}/photos?url=${encodeURIComponent(url)}`, { method: 'DELETE' }),
  adminMadeRealEligible: (q) => req('/admin/made-real/eligible-submissions' + qs(q ? { q } : {})),
  adminCleanBrokenImages: () => req('/admin/clean-broken-images', { method: 'POST' }),

  // ---- Admin gifted-rewards log ----
  adminRewards: (params = {}) => req('/admin/rewards' + qs(params)),
  adminRewardsExportURL: (params = {}) => `${BASE}/admin/rewards.csv` + qs(params),

  // Replies moderation (Phase 1 — "Build on this idea")
  adminReplies: (status = 'pending_review') => req(`/admin/replies?status=${encodeURIComponent(status)}`),
  adminReplyApprove: (id) => req(`/admin/replies/${encodeURIComponent(id)}/approve`, { method: 'POST' }),
  adminReplyReject:  (id) => req(`/admin/replies/${encodeURIComponent(id)}/reject`, { method: 'POST' }),
  adminReplyBanIp:   (id) => req(`/admin/replies/${encodeURIComponent(id)}/ban_ip`, { method: 'POST' }),
  adminReplyKillSwitch: () => req('/admin/replies/kill_switch'),

  // ---- Sticker library ----
  stickers: () => req('/stickers'),
  adminStickers: () => req('/admin/stickers'),
  adminStickerCreate: (body) => req('/admin/stickers', { method: 'POST', body: JSON.stringify(body) }),
  adminStickerPatch: (slug, body) => req(`/admin/stickers/${encodeURIComponent(slug)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminStickerDelete: (slug) => req(`/admin/stickers/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  adminAttachSticker: (sid, slug) => req(`/admin/submissions/${encodeURIComponent(sid)}/stickers/${encodeURIComponent(slug)}`, { method: 'POST' }),
  adminDetachSticker: (sid, slug) => req(`/admin/submissions/${encodeURIComponent(sid)}/stickers/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  adminStickerSuggest: (sid) => req(`/admin/submissions/${encodeURIComponent(sid)}/sticker-suggest`, { method: 'POST' }),

  // ---- Challenge Series Engine (Addendum 10) ----
  seriesList: () => req('/series'),
  seriesGet: (id) => req(`/series/${encodeURIComponent(id)}`),
  seriesLookupCode: (id, claim_code) => req(`/series/${encodeURIComponent(id)}/lookup-code`, { method: 'POST', body: JSON.stringify({ claim_code }) }),
  seriesWalk: (id, code) => req(`/series/${encodeURIComponent(id)}/walk/${encodeURIComponent(code)}`),
  communityStats: () => req('/community/stats'),
  who: (slug) => req(`/who/${encodeURIComponent(slug)}`),
  seriesComplete: (body) => req('/series/complete', { method: 'POST', body: JSON.stringify(body) }),
  adminSeries: () => req('/admin/series'),
  adminSeriesCreate: (body) => req('/admin/series', { method: 'POST', body: JSON.stringify(body) }),
  adminSeriesPatch: (id, body) => req(`/admin/series/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminSeriesDelete: (id) => req(`/admin/series/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminSeriesCompletions: (id, params = {}) => req(`/admin/series/${encodeURIComponent(id)}/completions` + qs(params)),
  adminSeriesRedeem: (id, code, outlet) => req(`/admin/series/${encodeURIComponent(id)}/completions/${encodeURIComponent(code)}/redeem` + qs({ outlet }), { method: 'POST' }),
  adminSeriesLookup: (code) => req(`/admin/series/lookup/${encodeURIComponent(code)}`),
  adminSeriesStuckWalkers: (seriesId) => req(`/admin/series/${encodeURIComponent(seriesId)}/stuck-walkers`),
  adminSeriesNudge: (seriesId) => req(`/admin/series/${encodeURIComponent(seriesId)}/nudge`, { method: 'POST' }),
  adminBowlWinners: () => req('/admin/menu/bowl-winners'),
  adminBowlNotify: (code) => req(`/admin/menu/bowl-winners/${encodeURIComponent(code)}/notify`, { method: 'POST' }),

  // ---- Mira admin inbox (Feb 2026) ----
  adminMiraList: (params = {}) => req('/admin/mira' + qs(params)),
  adminMiraFlag: (id, reason) => req(`/admin/mira/${encodeURIComponent(id)}/flag`, { method: 'POST', body: JSON.stringify({ reason: reason || '' }) }),
  adminMiraUnflag: (id) => req(`/admin/mira/${encodeURIComponent(id)}/unflag`, { method: 'POST' }),
  adminMiraReviewed: (id) => req(`/admin/mira/${encodeURIComponent(id)}/reviewed`, { method: 'POST' }),
  adminMiraFollowup: (id) => req(`/admin/mira/${encodeURIComponent(id)}/followup`, { method: 'POST' }),
  adminMiraSendZoho: (id) => req(`/admin/mira/${encodeURIComponent(id)}/send-to-zoho`, { method: 'POST' }),

  // ---- Safety Vault (admin-authed) ----
  adminVaultStatus: () => req('/admin/vault/status'),
  adminVaultBackupNow: () => req('/admin/vault/backup-now', { method: 'POST' }),

  // ---- Food Menu (public + admin CRUD) ----
  menu: () => req('/menu'),
  menuOrder: (body) => req('/menu/orders', { method: 'POST', body: JSON.stringify(body) }),
  adminMenuSeed: () => req('/admin/menu/seed', { method: 'POST' }),
  adminMenuSeedDefaultBadges: () => req('/admin/menu/seed-default-badges', { method: 'POST' }),
  partnersSettings: () => req('/partners/settings'),
  partnersApply: (body) => req('/partners/apply', { method: 'POST', body: JSON.stringify(body) }),
  adminPartnersLeads: () => req('/admin/partners/leads'),
  adminPartnersLeadsCsvURL: () => `${BASE}/admin/partners/leads.csv`,
  adminPartnersSettingsPatch: (body) => req('/admin/partners/settings', { method: 'PATCH', body: JSON.stringify(body) }),
  adminMenuCategories: () => req('/admin/menu/categories'),
  adminMenuCategoryCreate: (body) => req('/admin/menu/categories', { method: 'POST', body: JSON.stringify(body) }),
  adminMenuCategoryPatch: (slug, body) => req(`/admin/menu/categories/${encodeURIComponent(slug)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminMenuCategoryDelete: (slug) => req(`/admin/menu/categories/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  adminMenuItems: (category) => req('/admin/menu/items' + qs(category ? { category } : {})),
  adminMenuItemCreate: (body) => req('/admin/menu/items', { method: 'POST', body: JSON.stringify(body) }),
  adminMenuItemPatch: (id, body) => req(`/admin/menu/items/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminMenuItemDelete: (id) => req(`/admin/menu/items/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminMenuSettings: () => req('/admin/menu/settings'),
  adminMenuSettingsPatch: (body) => req('/admin/menu/settings', { method: 'PATCH', body: JSON.stringify(body) }),
  adminMenuOrders: (params) => req('/admin/menu/orders' + qs(params || {})),
  adminMenuOrderPatch: (id, body) => req(`/admin/menu/orders/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminMenuImportFromUrl: (url, opts = {}) => req('/admin/menu/import-from-url', { method: 'POST', body: JSON.stringify({ url, ...opts }) }),
  adminMenuBowlConfigs: () => req('/admin/menu/bowl-configs'),
  adminMenuBowlConfigUpsert: (item_slug, steps) => req(`/admin/menu/bowl-configs/${encodeURIComponent(item_slug)}`, { method: 'PUT', body: JSON.stringify({ steps }) }),
  adminMenuBowlConfigDelete: (item_slug) => req(`/admin/menu/bowl-configs/${encodeURIComponent(item_slug)}`, { method: 'DELETE' }),
  adminMenuDrivePreview: (folder) => req('/admin/menu/drive-preview', { method: 'POST', body: JSON.stringify({ folder }) }),
  adminMenuDriveApply: (picks) => req('/admin/menu/drive-apply', { method: 'POST', body: JSON.stringify({ picks }) }),
  adminMenuAttachDrive: (item_slug, drive_url) => req('/admin/menu/attach-drive', { method: 'POST', body: JSON.stringify({ item_slug, drive_url }) }),
  adminMenuUploadPhoto: (item_slug, file) => {
    // Multipart upload — do NOT set Content-Type; the browser fills the boundary.
    const fd = new FormData();
    fd.append('file', file);
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return fetch(`${base}/api/admin/menu/items/${encodeURIComponent(item_slug)}/upload-photo`, {
      method: 'POST', body: fd, credentials: 'include',
    }).then(async (r) => {
      const t = await r.text();
      let body = null; try { body = t ? JSON.parse(t) : null; } catch { /* non-JSON body */ }
      if (!r.ok) {
        const msg = body && (body.detail?.message || body.message) || `HTTP ${r.status}`;
        throw new Error(msg);
      }
      return body;
    });
  },

  // ---- Menu Drops (community-voted upcoming dishes) ----
  menuDropActive: () => req('/menu-drops/active'),
  menuDropVote: (slug, option_label) => req(`/menu-drops/${encodeURIComponent(slug)}/vote`, { method: 'POST', body: JSON.stringify({ option_label }) }),
  menuDropSuggest: (slug, body) => req(`/menu-drops/${encodeURIComponent(slug)}/suggest`, { method: 'POST', body: JSON.stringify(body) }),
  adminMenuDrops: () => req('/admin/menu/menu-drops'),
  adminMenuDropCreate: (body) => req('/admin/menu/menu-drops', { method: 'POST', body: JSON.stringify(body) }),
  adminMenuDropPatch: (slug, body) => req(`/admin/menu/menu-drops/${encodeURIComponent(slug)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminMenuDropDelete: (slug) => req(`/admin/menu/menu-drops/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  adminMenuDropPromote: (slug, body) => req(`/admin/menu/menu-drops/${encodeURIComponent(slug)}/promote`, { method: 'POST', body: JSON.stringify(body) }),
  adminMenuDropOptionApprove: (slug, label) => req(`/admin/menu/menu-drops/${encodeURIComponent(slug)}/options/${encodeURIComponent(label)}/approve`, { method: 'POST' }),
  adminMenuDropOptionDelete: (slug, label) => req(`/admin/menu/menu-drops/${encodeURIComponent(slug)}/options/${encodeURIComponent(label)}`, { method: 'DELETE' }),

  // ---- Cross-outlet syndication ----
  adminMenuAdoptable: (outlet) => req('/admin/menu/adoptable' + qs({ outlet })),
  adminMenuAdopt: (itemId, body) => req(`/admin/menu/items/${encodeURIComponent(itemId)}/adopt`, { method: 'POST', body: JSON.stringify(body) }),

  // ---- Xin Codex — "Sign your name in Xin" ----
  codexSign: (body) => req('/codex/sign', { method: 'POST', body: JSON.stringify(body) }),
  codexList: (limit = 100) => req('/codex' + qs({ limit })),
  codexCount: () => req('/codex/count'),
  codexChapters: () => req('/codex/chapters'),
  pulseOverhear: (limit = 20) => req('/pulse/overhear' + qs({ limit })),

  // ---- Community Admin (customer intelligence) ----
  adminCommunityList: (q = '', limit = 500) => req('/admin/community/customers' + qs({ q, limit })),
  adminCommunityTimeline: (email) => req(`/admin/community/customers/${encodeURIComponent(email)}`),
  adminCommunityDelete: (email) => req(`/admin/community/customers/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  adminCommunityNote: (email, note) => req(`/admin/community/customers/${encodeURIComponent(email)}/note`, { method: 'PUT', body: JSON.stringify({ note }) }),
  adminCommunityTemplates: () => req('/admin/community/message-templates'),
  adminCommunityMessage: (email, subject, body) => req(`/admin/community/customers/${encodeURIComponent(email)}/message`, { method: 'POST', body: JSON.stringify({ subject, body }) }),
  adminCommunityBroadcast: (chapter_id, subject, body) => req('/admin/community/broadcast', { method: 'POST', body: JSON.stringify({ chapter_id, subject, body }) }),

  // ---- Weekly digest ----
  adminWeeklyDigestPreview: () => req('/admin/weekly-digest/preview'),
  adminWeeklyDigestSendTest: (to = '') => req('/admin/weekly-digest/send-test', { method: 'POST', body: JSON.stringify({ to }) }),

  // ---- Bowl Story · Drive → Cloudinary import ----
  adminBowlStoryImport: (itemId, url) => req(`/admin/menu/items/${itemId}/import-bowl-story`, { method: 'POST', body: JSON.stringify({ url }) }),
  adminBowlStoriesBulkMigrate: () => req('/admin/menu/bulk-migrate-drive-stories', { method: 'POST' }),
  adminBowlStoriesFillPosters: (opts) => req('/admin/menu/fill-missing-posters', { method: 'POST', body: JSON.stringify(opts || {}) }),

  // ---- Guardians (About team) ----
  guardiansList: () => req('/guardians'),
  adminGuardiansList: () => req('/admin/guardians'),
  adminGuardianCreate: (formData) => req('/admin/guardians', { method: 'POST', body: formData }),  // multipart
  adminGuardianUpdate: (id, body) => req(`/admin/guardians/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminGuardianDelete: (id) => req(`/admin/guardians/${id}`, { method: 'DELETE' }),
  adminGuardiansReorder: (ids) => req('/admin/guardians/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
  adminGuardiansSeed: () => req('/admin/guardians/seed', { method: 'POST' }),

  // ── Blog / Journal ─────────────────────────────────────────────────
  blogList: () => req('/blog/posts'),
  blogGet: (slug) => req(`/blog/posts/${encodeURIComponent(slug)}`),
  adminBlogList: () => req('/admin/blog/posts'),
  adminBlogCreate: (body) => req('/admin/blog/posts', { method: 'POST', body: JSON.stringify(body) }),
  adminBlogPatch: (id, body) => req(`/admin/blog/posts/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminBlogDelete: (id) => req(`/admin/blog/posts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminBlogUploadImage: (file, hint = 'blog') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('hint', hint);
    return req('/admin/blog/upload-image', { method: 'POST', body: fd });
  },
  adminBlogAiIdeas: (seed) => req('/admin/blog/ai/suggest-ideas', { method: 'POST', body: JSON.stringify({ seed: seed || '' }), noRetry: true }),
  adminBlogAiDraft: (payload) => req('/admin/blog/ai/draft', { method: 'POST', body: JSON.stringify(payload), noRetry: true }),
  adminBlogAiSeoReview: (post) => req('/admin/blog/ai/seo-review', { method: 'POST', body: JSON.stringify(post), noRetry: true }),
  adminBlogAiImages: (hint) => req('/admin/blog/ai/suggest-images', { method: 'POST', body: JSON.stringify({ hint }), noRetry: true }),
  adminBlogLibrary: (q = '', folder = '') => req(`/admin/blog/library?q=${encodeURIComponent(q)}&folder=${encodeURIComponent(folder)}&limit=40`),

  // ── Xin Storyteller (Instagram/Facebook/LinkedIn/GBP/WhatsApp) ────
  storytellerPillars: () => req('/admin/storyteller/pillars'),
  storytellerDraft: (payload) => req('/admin/storyteller/draft', { method: 'POST', body: JSON.stringify(payload), noRetry: true }),
  storytellerWeek: () => req('/admin/storyteller/week', { method: 'POST', noRetry: true }),
  storytellerCountdown: (payload) => req('/admin/storyteller/countdown', { method: 'POST', body: JSON.stringify(payload), noRetry: true }),

  // Storyteller queue (save · edit · schedule · publish)
  storytellerQueueList: (status = '') => req(`/admin/storyteller/queue${status ? '?status=' + encodeURIComponent(status) : ''}`),
  storytellerQueueCreate: (payload) => req('/admin/storyteller/queue', { method: 'POST', body: JSON.stringify(payload) }),
  storytellerQueuePatch: (id, patch) => req(`/admin/storyteller/queue/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  storytellerQueueDelete: (id) => req(`/admin/storyteller/queue/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  storytellerQueuePublish: (id) => req(`/admin/storyteller/queue/${encodeURIComponent(id)}/publish`, { method: 'POST' }),
};

function qs(params) {
  const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return p.length ? '?' + new URLSearchParams(Object.fromEntries(p)).toString() : '';
}
